import { ArrowsClockwise, CheckCircle, Link } from "@phosphor-icons/react";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import StatCard from "../../../../components/StatCard";
import { isToday, parseISO,format, isYesterday, formatDistanceToNow } from "date-fns";

interface IntegrationStatsProps {
    activePairs: number;
    activeOnly: number;
    ongoingPairs: number;
    syncHealth: number;
    lastPull: string;
}

const IntegrationStats: React.FC<IntegrationStatsProps> = ({
    activePairs,
    activeOnly,
    ongoingPairs,
    syncHealth,
    lastPull,
}) => {
    const { t } = useTranslation();

    const lastSyncedTooltipText = useMemo(() => {
        const lastSynced = lastPull;
    
        if (!lastSynced) return "";
    
        const lastSyncedDate = parseISO(lastSynced);
    
        if (isToday(lastSyncedDate)) {
          return t("common.lastSynced.today", {
            time: format(lastSyncedDate, "h:mm a"),
          });
        } else if (isYesterday(lastSyncedDate)) {
          return t("common.lastSynced.yesterday", {
            time: format(lastSyncedDate, "h:mm a"),
          });
        } else {
          return t("common.lastSynced.date", {
            date: format(lastSyncedDate, "MMM d"),
            time: format(lastSyncedDate, "h:mm a"),
          });
        }
      }, [t]);
      
      const lastPullValue = useMemo(() => {
          if (!lastPull) return "-";
          try {
            return formatDistanceToNow(new Date(lastPull), {
              addSuffix: true,
            });
          } catch (e) {
            console.error("Error formatting sync time:", e);
            return "N/A";
          }
        }, [lastPull, t]);
    

    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <StatCard
                title={t("configurations.integrations.stats.connectedPairs")}
                value={activePairs}
                icon={<Link size={24} weight="duotone" />}
                color="indigo"
                subStats={[
                    { label: "Active", value: activeOnly },
                    { label: "Ongoing", value: ongoingPairs },
                ]}
            />
            <StatCard
                title={t("configurations.integrations.stats.systemHealth")}
                value={t("configurations.integrations.stats.syncPercentage", { percentage: syncHealth })}
                icon={<ArrowsClockwise size={24} weight="duotone" />}
                color="indigo"
            />
            <StatCard
                title={t("configurations.integrations.stats.lastDataPull")}
                value={lastPullValue}
                icon={<CheckCircle size={24} weight="duotone" />}
                color="indigo"
                tooltip={true}
                tooltipContent={lastSyncedTooltipText}
                tooltipDirection="right"
            />
        </div>
    );
};

export default IntegrationStats;
